import { Link } from "react-router-dom";
import { CopyButton } from "@/features/ordini/components/OrderCard";
import { InvoiceInfo } from "@/shared/ui/InvoiceInfo";
import { formatDateTime, formatMoney } from "@/shared/format";
import { Pill, Riga, Righe, Sezione, Vuoto } from "./parti";
import type { CallSummary, ContextOrder, ThreadContextResponse } from "./tipi";

//: Motivo chiamata CRM → etichetta italiana. Chiavi = enum ``CallReason``
//: del backend (crm/schemas.py); fallback al valore grezzo per motivi futuri.
const CALL_REASON_LABEL: Record<string, string> = {
  info_spedizione: "Info spedizione",
  reclamo: "Reclamo",
  reso_sostituzione: "Reso / sostituzione",
  info_prodotto: "Info prodotto",
  sollecito_pagamento: "Sollecito pagamento",
  altro: "Altro",
};

const STATE_LABEL: Record<string, string> = {
  nuovo: "Nuovo",
  in_lavorazione: "In lavorazione",
  pronto: "Pronto",
  spedito: "Spedito",
  annullato: "Annullato",
};

function Copiabile({
  testo,
  label,
  mono = false,
}: {
  testo: string;
  label: string;
  mono?: boolean;
}) {
  return (
    <>
      <span className={`min-w-0 break-all ${mono ? "tabular-nums text-[12px]" : ""}`}>{testo}</span>
      <CopyButton text={testo} label={label} />
    </>
  );
}

/** Contatti, indirizzo e storico del cliente. */
export function Cliente({ order, data }: { order: ContextOrder; data: ThreadContextResponse }) {
  const c360 = data.customer360;
  const luogo = [order.shipping_postcode, order.shipping_city].filter(Boolean).join(" ");
  const indirizzo = [
    order.shipping_address,
    luogo && order.shipping_province ? `${luogo} (${order.shipping_province})` : luogo,
    order.shipping_country && order.shipping_country !== "IT" ? order.shipping_country : "",
  ]
    .filter(Boolean)
    .join(", ");
  return (
    <Sezione titolo="Cliente">
      <Righe>
        <Riga label="Email">
          {order.customer_email ? (
            <Copiabile testo={order.customer_email} label="Copia email" />
          ) : (
            "—"
          )}
        </Riga>
        {order.customer_phone && (
          <Riga label="Telefono">
            <Copiabile testo={order.customer_phone} label="Copia telefono" mono />
          </Riga>
        )}
        <Riga label="Indirizzo">
          <span className="font-normal">{indirizzo || "—"}</span>
        </Riga>
      </Righe>
      {c360 && (
        <div className="border-t border-line pt-2">
          <div className="text-[12.5px] text-ink">
            <b>
              {c360.orders_count} {c360.orders_count === 1 ? "ordine" : "ordini"}
            </b>{" "}
            · {formatMoney(Number(c360.ltv), c360.currency)} in totale (lordo)
          </div>
          {c360.is_fallback_key && (
            <div className="mt-0.5 text-[11px] text-ink">
              match debole: cliente riconosciuto per nome+città (email assente)
            </div>
          )}
          {c360.other_orders.map((o) => (
            <Link
              key={o.reference}
              to={`/ordini/${encodeURIComponent(o.reference)}`}
              className="mt-1 flex items-center justify-between gap-2 border-t border-line py-1.5 hover:bg-paper"
            >
              <span className="min-w-0">
                <span className="block truncate tabular-nums text-[11.5px] font-semibold text-ink">
                  {o.reference}
                </span>
                <span className="block text-[11px] text-ink">
                  {[formatDateTime(o.date), STATE_LABEL[o.state] || o.state]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <span className="shrink-0 text-[12px] font-semibold tabular-nums text-ink">
                {formatMoney(Number(o.total), c360.currency)}
              </span>
            </Link>
          ))}
        </div>
      )}
    </Sezione>
  );
}

/** Rimborso e fattura: quello che il cliente chiede quando parla di soldi. */
export function Soldi({ data, currency }: { data: ThreadContextResponse; currency: string }) {
  const refund = data.refund;
  return (
    <Sezione titolo="Soldi e documenti">
      <Righe>
        <Riga label="Rimborso">
          {!refund ? (
            <span className="font-normal">nessuno</span>
          ) : refund.amount == null ? (
            <Pill tono="giallo">Rilevato, importo da confermare</Pill>
          ) : (
            <Pill tono="giallo">{formatMoney(Number(refund.amount), currency)}</Pill>
          )}
        </Riga>
        {!data.invoice && (
          <Riga label="Fattura">
            <span className="font-normal">non ancora emessa</span>
          </Riga>
        )}
      </Righe>
      <InvoiceInfo invoice={data.invoice} className="!mb-0 !border-0 !p-0" />
    </Sezione>
  );
}

export function Chiamate({ calls }: { calls: CallSummary[] }) {
  return (
    <Sezione titolo="Chiamate">
      {calls.length === 0 ? (
        <Vuoto>Nessuna chiamata registrata</Vuoto>
      ) : (
        calls.map((c, i) => (
          <div key={`${c.reason}-${c.created_at ?? i}`} className="text-[12.5px] text-ink">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold">{CALL_REASON_LABEL[c.reason] || c.reason}</span>
              <span className="shrink-0 text-[11px] tabular-nums">
                {formatDateTime(c.created_at)}
              </span>
            </div>
            {c.note && <div className="mt-0.5 line-clamp-2 text-[11.5px]">{c.note}</div>}
          </div>
        ))
      )}
    </Sezione>
  );
}
